import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useEndpoints } from "../api/ApiContext";
import { ApiError, totpReuseHint } from "../api/errors";
import { Banner, Button, Input } from "../components/ui";
import { OAUTH_CLIENT_ID, oauthRedirectUri } from "../config";
import { generatePkcePair, type PkcePair } from "../lib/pkce";
import { getAssertion, WebAuthnCancelledError, webAuthnSupported } from "../lib/webauthn";
import { RateLimitNotice, useRateLimit } from "./rateLimit";

// OAuth2 Authorization Code + PKCE (S256), first-party, per UX-DESIGN.md
// §4.1 and ARCHITECTURE.md §2:
//
//   1. POST /v1/auth/login {client_id, email, password, redirect_uri, code_challenge}
//        -> {mfa_required:false, code}               (no factor enrolled), or
//        -> {mfa_required:true, mfa_challenge}       (5-minute sealed token)
//   2. MFA, one of:
//        POST /v1/auth/mfa/verify {mfa_challenge, code, use_recovery_code:false}  (TOTP)
//        POST /v1/auth/mfa/verify {mfa_challenge, code, use_recovery_code:true}   (recovery code)
//        POST /v1/auth/mfa/webauthn/login/begin|finish                           (security key)
//        -> {code}
//   3. POST /v1/auth/token {code, code_verifier} -> Set-Cookie vads_session + {csrf_token}
//
// The PKCE verifier never leaves this component's memory; only its S256
// challenge is sent in step 1, and the verifier only in step 3.
//
// Login and MFA verification are rate limited (429 rate_limited +
// Retry-After, auth_ratelimit.go): the screen shows a countdown and keeps
// its buttons disabled until it ends.

export interface LoginResult {
  csrfToken: string;
  mfaEnrolled: boolean;
}

type MfaMethod = "totp" | "key" | "recovery";

type Step = { kind: "credentials" } | { kind: "mfa"; challenge: string; method: MfaMethod };

export function LoginFlow({ onAuthenticated }: { onAuthenticated: (r: LoginResult) => Promise<void> | void }) {
  const ep = useEndpoints();
  const pkce = useRef<PkcePair | null>(null);
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Shown under a rejected TOTP code: a reused code and a wrong one get the same 401.
  const [hint, setHint] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ email?: string; password?: string; code?: string }>({});
  const [busy, setBusy] = useState(false);
  const rl = useRateLimit();

  async function exchange(authCode: string, mfaEnrolled: boolean) {
    const verifier = pkce.current?.verifier;
    if (!verifier) throw new Error("Sign-in state was lost. Start again.");
    const { csrf_token } = await ep.auth.exchangeToken({ code: authCode, code_verifier: verifier });
    pkce.current = null;
    await onAuthenticated({ csrfToken: csrf_token, mfaEnrolled });
  }

  function handleError(err: unknown) {
    setHint(null);
    if (rl.capture(err)) {
      setError(null);
      return;
    }
    if (err instanceof ApiError && err.code?.startsWith("auth: invalid or expired authorization code")) {
      // The MFA challenge (5 min) or the code (2 min) expired: start over.
      setStep({ kind: "credentials" });
      setCode("");
      setPassword("");
    }
    setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
  }

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    const fe: typeof fieldError = {};
    if (!email.trim()) fe.email = "Enter your email address.";
    if (!password) fe.password = "Enter your password.";
    setFieldError(fe);
    if (fe.email || fe.password) return;
    setBusy(true);
    setError(null);
    try {
      pkce.current = await generatePkcePair();
      const res = await ep.auth.login({
        client_id: OAUTH_CLIENT_ID,
        email: email.trim(),
        password,
        redirect_uri: oauthRedirectUri(),
        code_challenge: pkce.current.challenge,
      });
      if (res.mfa_required) {
        setStep({ kind: "mfa", challenge: res.mfa_challenge, method: webAuthnSupported() ? "key" : "totp" });
      } else {
        await exchange(res.code, false);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (step.kind !== "mfa") return;
    const recovery = step.method === "recovery";
    const value = recovery ? code.trim() : code.replace(/\s/g, "");
    if (!recovery && !/^\d{6}$/.test(value)) {
      setFieldError({ code: "Enter the 6-digit code from your authenticator app." });
      return;
    }
    if (recovery && !value) {
      setFieldError({ code: "Enter one of your recovery codes." });
      return;
    }
    setFieldError({});
    setBusy(true);
    setError(null);
    try {
      const res = await ep.auth.verifyMfa({ mfa_challenge: step.challenge, code: value, use_recovery_code: recovery });
      await exchange(res.code, true);
    } catch (err) {
      handleError(err);
      if (!recovery) setHint(totpReuseHint(err));
    } finally {
      setBusy(false);
    }
  }

  async function useKey() {
    if (step.kind !== "mfa") return;
    setBusy(true);
    setError(null);
    try {
      const { options } = await ep.auth.webauthn.loginBegin(step.challenge);
      const credential = await getAssertion(options);
      const res = await ep.auth.webauthn.loginFinish(step.challenge, credential);
      await exchange(res.code, true);
    } catch (err) {
      if (err instanceof WebAuthnCancelledError) setError(err.message);
      else if (err instanceof ApiError && err.code === "auth: mfa not enrolled")
        setError("No security key is registered on this account. Use your authenticator app instead.");
      else handleError(err);
    } finally {
      setBusy(false);
    }
  }

  function switchMethod(method: MfaMethod) {
    if (step.kind !== "mfa") return;
    setStep({ ...step, method });
    setCode("");
    setError(null);
    setHint(null);
    setFieldError({});
  }

  if (step.kind === "credentials") {
    return (
      <form className="stack" onSubmit={submitCredentials} noValidate aria-describedby={error ? "login-error" : undefined}>
        {error ? (
          <div id="login-error">
            <Banner tone="error" live="alert">
              {error}
            </Banner>
          </div>
        ) : null}
        <RateLimitNotice state={rl} />
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldError.email}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldError.password}
          required
        />
        <Button type="submit" block loading={busy} disabled={rl.limited}>
          Continue
        </Button>
        <div className="row-between small">
          <Link to="/recover">Can't sign in?</Link>
          <Link to="/signup">Create an account</Link>
        </div>
      </form>
    );
  }

  return (
    <div className="stack">
      <div className="stack-sm">
        <h2 style={{ fontSize: "var(--text-h3)" }}>Two-step verification</h2>
        <p className="muted small">Signing in as {email}.</p>
      </div>
      {error ? (
        <Banner tone="error" live="alert">
          <div className="stack-sm">
            <span>{error}</span>
            {hint ? <span className="small">{hint}</span> : null}
          </div>
        </Banner>
      ) : null}
      <RateLimitNotice state={rl} />

      {step.method === "key" ? (
        <div className="stack">
          <p>Use the security key or passkey registered to this account. You'll be asked for its PIN or biometric.</p>
          <Button block onClick={useKey} loading={busy} disabled={rl.limited}>
            Use security key
          </Button>
        </div>
      ) : (
        <form className="stack" onSubmit={submitCode} noValidate>
          <Input
            label={step.method === "recovery" ? "Recovery code" : "6-digit code"}
            hint={
              step.method === "recovery"
                ? "Each recovery code works once. After signing in, set up a new device."
                : "From your authenticator app."
            }
            inputMode={step.method === "recovery" ? "text" : "numeric"}
            autoComplete="one-time-code"
            mono
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={fieldError.code}
            autoFocus
          />
          <Button type="submit" block loading={busy} disabled={rl.limited}>
            Verify
          </Button>
        </form>
      )}

      <div className="stack-sm small" role="group" aria-label="Other ways to verify">
        {step.method !== "totp" ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => switchMethod("totp")} disabled={busy}>
            Use an authenticator app code
          </button>
        ) : null}
        {step.method !== "key" && webAuthnSupported() ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => switchMethod("key")} disabled={busy}>
            Use a security key or passkey
          </button>
        ) : null}
        {step.method !== "recovery" ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => switchMethod("recovery")} disabled={busy}>
            Use a recovery code
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setStep({ kind: "credentials" });
            setPassword("");
            setError(null);
          }}
          disabled={busy}
        >
          Start over
        </button>
      </div>
    </div>
  );
}
